import Joi from "joi";
import { cloneDeep, isEqual, isPlainObject, omit } from "lodash";
import {
  IQuestionnaire,
  IQuestionnaireDisplayLogicType,
  IQuestionnaireDisplayRuleConditionInputNumberOperator,
  IQuestionnaireDisplayRuleConditionOperator,
  IQuestionnaireDisplayRuleConditionType,
  IQuestionnaireDisplayRuleSourceType,
  IQuestionnaireDisplayRuleTargetType,
  IQuestionnaireQuestion,
  IQuestionnaireSection,
  ITask,
  ITaskSchedule,
  QuestionnaireQuestionType,
  ReminderItem,
  ReminderType,
  TaskType,
} from "../shared/db";
import {
  QuestionnaireTempInputConfigOptions,
  QuestionnaireTempQuestion,
  QuestionnaireTempSection,
  TempQuestionnaire,
} from "./task";
import {
  fetchDisplayStrategyListFromQuestionnaire,
  TempQuestionnaireDisplayStrategyUpsertItem,
  UpsertDisplayRule,
} from "./questionnaire_display_strategy";
import {
  ActionableTempTaskSchedule,
  getTaskScheduleSchemaMap,
  TaskScheduleSettingType,
  TempTaskScheduleActionType,
} from "./schedule";
import { hashString } from "../lib/questionnaire_draft/hash";

// The recovery copy of a questionnaire task editor. It is defined here, apart from the sanitizers the
// real save uses, so the two can change independently; any change to the snapshot shape needs a new
// version, because older copies stay on the server until they expire.
export const QUESTIONNAIRE_DRAFT_SCHEMA_VERSION = 1;

/* ------------------------------ target ------------------------------ */

export type QuestionnaireDraftTarget =
  | { kind: "new"; projectId: string; methodId: string }
  | { kind: "existing"; projectId: string; methodId: string; taskId: string };

export interface QuestionnaireDraftEditorContext {
  projectId: string;
  methodId: string;
  // the task the editor was opened for, if any; it may be a staged live-project task that was never saved
  editingTaskId?: string;
  // tasks the method has actually saved
  savedTaskIdList: string[];
}

// Only a task the method has saved is an existing target. The 'taskDraft' placeholder and the frontend
// UUID of a staged live-project task are new questionnaires and share the project's single new slot.
export const deriveQuestionnaireDraftTarget = (
  context: QuestionnaireDraftEditorContext,
): QuestionnaireDraftTarget => {
  const { projectId, methodId, editingTaskId, savedTaskIdList } = context;
  if (editingTaskId && savedTaskIdList.includes(editingTaskId)) {
    return { kind: "existing", projectId, methodId, taskId: editingTaskId };
  }
  return { kind: "new", projectId, methodId };
};

export interface QuestionnaireDraftKey {
  projectId: string;
  taskId?: string;
}

// What a staged live-project task carries until it is published: which copy it was staged from
export interface QuestionnaireDraftStagedSave {
  nonce: string;
  key: QuestionnaireDraftKey;
  slotInstance: number;
  revision: number;
}

export const getQuestionnaireDraftKey = (
  target: QuestionnaireDraftTarget,
): QuestionnaireDraftKey =>
  target.kind === "existing"
    ? { projectId: target.projectId, taskId: target.taskId }
    : { projectId: target.projectId };

/* ------------------------------ base ------------------------------ */

export interface QuestionnaireDraftSavedBase {
  taskUpdatedAt: string;
  fingerprint: string;
}

export enum QuestionnaireDraftSaveStage {
  Content = "content",
  DisplayStrategy = "display_strategy",
  Schedule = "schedule",
}

// Evidence of a save that stopped part-way, kept under the original key.
export interface QuestionnaireDraftCheckpoint {
  taskId: string;
  confirmedStageList: QuestionnaireDraftSaveStage[];
  base: QuestionnaireDraftSavedBase;
  // temporary editor id -> id the server assigned
  idMap: Record<string, string>;
}

export interface QuestionnaireDraftSubmissionMarker {
  revision: number;
  fingerprint: string;
}

export interface QuestionnaireDraftBase {
  // the saved task this edit started from; required for an existing task, null for a new one
  saved: QuestionnaireDraftSavedBase | null;
  checkpoint: QuestionnaireDraftCheckpoint | null;
  submission: QuestionnaireDraftSubmissionMarker | null;
}

/* ------------------------------ working state (editor side) ------------------------------ */

export interface QuestionnaireDraftInputBuffer {
  field: string;
  text: string;
}

export interface DisplayRuleEntryControls {
  sourceQuestionId: string;
  conditionOptionId?: string;
  conditionOperator: string;
  conditionValue: number;
  conditionValue2: number;
}

export interface SkipRuleEntryControls {
  conditionOperator: string;
  conditionValue: number;
  conditionValue2: number;
  targetValue: string;
}

export interface DisplayLogicEditorDraftState {
  targetType: "section" | "question";
  itemId: string;
  enabledDisplayLogic: boolean;
  enabledQuestionSkipLogic: boolean;
  afterThisSection: string;
  displayRuleList: UpsertDisplayRule[];
  skipRuleList: UpsertDisplayRule[];
  // the entry controls can be newer than the rule lists while their debounce is pending
  displayRuleControlsList: DisplayRuleEntryControls[];
  skipRuleControlsList: SkipRuleEntryControls[];
}

export interface QuestionnaireEditorDraftState {
  sectionList: QuestionnaireTempSection[];
  editingBlockId: string | null;
  inputBufferList: QuestionnaireDraftInputBuffer[];
  displayLogic: DisplayLogicEditorDraftState | null;
}

export interface QuestionnaireDraftTaskFormValues {
  name?: string;
  description?: string;
  scheduleRepeat?: string;
  scheduleDescription?: string;
  reminderList?: ReminderItem[];
}

export interface QuestionnaireDraftWorkingState {
  target: QuestionnaireDraftTarget;
  revision: number;
  // a new questionnaire's create key: the server answers a second create with it by naming the task
  // the first one made, so a copy whose create went through cannot create the questionnaire again
  createKey?: string;
  base: QuestionnaireDraftBase;
  form: QuestionnaireDraftTaskFormValues & {
    color: string;
    scheduleSettingType: TaskScheduleSettingType;
  };
  schedule: {
    baselineScheduleIdList: string[];
    taskScheduleQueue: ActionableTempTaskSchedule[];
    liveEditingScheduleQueue: ActionableTempTaskSchedule[];
  };
  // what the outer task form holds; an open inner editor keeps its own working copy in `editor`
  questionnaire: TempQuestionnaire | IQuestionnaire | null;
  displayStrategyList: TempQuestionnaireDisplayStrategyUpsertItem[];
  editor: QuestionnaireEditorDraftState | null;
}

/* ------------------------------ snapshot (JSON) ------------------------------ */

type JsonNumbers<T> = { [K in keyof T]-?: T[K] extends number ? number | null : T[K] };

export type QuestionnaireDraftDisplayRuleControls = Omit<
  JsonNumbers<DisplayRuleEntryControls>,
  "conditionOptionId"
> & {
  conditionOptionId: string | null;
};
export type QuestionnaireDraftSkipRuleControls = JsonNumbers<SkipRuleEntryControls>;

export interface QuestionnaireDraftRule {
  source?: {
    type: IQuestionnaireDisplayRuleSourceType;
    questionnaireQuestionId?: string;
    questionnaireSectionId?: string;
  } | null;
  condition?: {
    type: IQuestionnaireDisplayRuleConditionType;
    operator?: IQuestionnaireDisplayRuleConditionOperator;
    // null stands for a value that is not a finite number
    inputNumberList?: {
      operator: IQuestionnaireDisplayRuleConditionInputNumberOperator;
      value: number | null;
    }[];
    inputOptionList?: { optionId?: string }[];
  } | null;
  target?: {
    type: IQuestionnaireDisplayRuleTargetType;
    questionnaireSectionId?: string;
    questionnaireQuestionId?: string;
  } | null;
}

export interface QuestionnaireDraftDisplayStrategyItem {
  sectionId?: string;
  questionId?: string;
  displayStrategyId?: string;
  displayStrategy: {
    displayLogicList?: {
      type: IQuestionnaireDisplayLogicType;
      displayRuleList?: QuestionnaireDraftRule[];
    }[];
  };
}

export interface QuestionnaireDraftQuestion {
  id: string;
  questionnaireId?: string;
  questionnaireSectionId?: string;
  index?: number;
  displayStrategyId?: string;
  label: string;
  type: QuestionnaireQuestionType;
  description: string;
  required: boolean;
  mediaUrl?: string | null;
  inputConfig?: QuestionnaireTempInputConfigOptions | null;
}

export interface QuestionnaireDraftSection {
  id: string;
  questionnaireId?: string;
  index?: number;
  displayStrategyId?: string;
  legend: string;
  description: string;
  mediaUrl?: string | null;
  questionList: QuestionnaireDraftQuestion[];
}

export interface QuestionnaireDraftQuestionnaire {
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  taskId: string;
  projectId: string;
  workspaceId: string;
  name: string;
  description: string;
  // what the researcher currently sees: the open inner editor's copy, or the committed one
  sectionList: QuestionnaireDraftSection[];
}

export interface QuestionnaireDraftDisplayLogicEditor {
  targetType: "section" | "question";
  itemId: string;
  enabledDisplayLogic: boolean;
  enabledQuestionSkipLogic: boolean;
  afterThisSection: string;
  displayRuleList: QuestionnaireDraftRule[];
  skipRuleList: QuestionnaireDraftRule[];
  displayRuleControlsList: QuestionnaireDraftDisplayRuleControls[];
  skipRuleControlsList: QuestionnaireDraftSkipRuleControls[];
}

export interface QuestionnaireDraftEditor {
  // the committed sections an inner Cancel returns to; null when the open editor has no section changes
  rollbackSectionList: QuestionnaireDraftSection[] | null;
  editingBlockId: string | null;
  inputBufferList: QuestionnaireDraftInputBuffer[];
  displayLogic: QuestionnaireDraftDisplayLogicEditor | null;
}

export type QuestionnaireDraftScheduleValues = Record<string, unknown>;

export interface QuestionnaireDraftSchedule {
  tempId: string;
  createdAt: string;
  action: TempTaskScheduleActionType | null;
  reminderListChanged: boolean | null;
  values: QuestionnaireDraftScheduleValues;
}

export interface QuestionnaireDraftTaskForm {
  name: string;
  description: string;
  scheduleRepeat: string | null;
  scheduleDescription: string | null;
  reminderList: ReminderItem[] | null;
  color: string;
  scheduleSettingType: TaskScheduleSettingType;
}

export interface QuestionnaireDraftSnapshot {
  version: typeof QUESTIONNAIRE_DRAFT_SCHEMA_VERSION;
  target: QuestionnaireDraftTarget;
  revision: number;
  // a new questionnaire's create key; an existing task's copy has none
  createKey?: string;
  base: QuestionnaireDraftBase;
  form: QuestionnaireDraftTaskForm;
  schedule: {
    baselineScheduleIdList: string[];
    taskScheduleQueue: QuestionnaireDraftSchedule[];
    liveEditingScheduleQueue: QuestionnaireDraftSchedule[];
  };
  questionnaire: QuestionnaireDraftQuestionnaire | null;
  // the committed display-logic upsert list
  displayStrategyList: QuestionnaireDraftDisplayStrategyItem[];
  editor: QuestionnaireDraftEditor | null;
}

// What an open questionnaire task editor offers the code that keeps its recovery copy.
export interface QuestionnaireDraftEditorHandle {
  // the complete snapshot, read synchronously, including text not blurred yet and logic entries still inside
  // their debounce; null when the editor takes no part in drafts
  readSnapshot: () => QuestionnaireDraftSnapshot | null;
  // applies a decoded snapshot to the task form, the inner editor, display logic and schedule intent at once
  hydrate: (snapshot: QuestionnaireDraftSnapshot) => void;
  // the copy was discarded, or the researcher went on without it: a create from here on is a new one
  renewCreateKey: () => void;
}

/* ------------------------------ encoding ------------------------------ */

const pickDefined = <T extends object>(value: T): Partial<T> => {
  const result: Partial<T> = {};
  for (const key of Object.keys(value) as (keyof T)[]) {
    if (value[key] !== undefined) result[key] = value[key];
  }
  return result;
};

const toIsoString = (value: Date | string) => (value instanceof Date ? value.toISOString() : value);

const finiteOrNull = (value: number) => (Number.isFinite(value) ? value : null);
const nullToNaN = (value: number | null) => (value === null ? NaN : value);

// a blob:, data: or filesystem: URL is a local file that never finished uploading
const isCompletedMediaUrl = (mediaUrl: string) => !/^(blob|data|filesystem):/i.test(mediaUrl);
const encodeMediaUrl = (mediaUrl: string | null | undefined) =>
  mediaUrl === undefined
    ? {}
    : { mediaUrl: mediaUrl !== null && isCompletedMediaUrl(mediaUrl) ? mediaUrl : null };

const encodeInputConfig = (
  inputConfig: QuestionnaireTempInputConfigOptions,
): QuestionnaireTempInputConfigOptions => {
  const {
    customOtherName,
    allowOther,
    showSlider,
    integer,
    max,
    min,
    maxLabel,
    minLabel,
    options,
  } = inputConfig;
  return pickDefined({
    customOtherName,
    allowOther,
    showSlider,
    integer,
    max: max !== undefined && Number.isFinite(max) ? max : undefined,
    min: min !== undefined && Number.isFinite(min) ? min : undefined,
    maxLabel,
    minLabel,
    options: options?.map(
      ({ id, value, name }) => pickDefined({ id, value, name }) as { value: string; name: string },
    ),
  });
};

const encodeQuestion = (
  question: QuestionnaireTempQuestion | IQuestionnaireQuestion,
): QuestionnaireDraftQuestion => {
  const { id, questionnaireId, questionnaireSectionId, index, displayStrategyId, inputConfig } =
    question;
  return {
    ...pickDefined({ id, questionnaireId, questionnaireSectionId, index, displayStrategyId }),
    id,
    label: question.label,
    type: question.type,
    description: question.description,
    required: question.required,
    ...encodeMediaUrl(question.mediaUrl),
    ...(inputConfig === undefined
      ? {}
      : {
          inputConfig:
            inputConfig === null
              ? null
              : encodeInputConfig(inputConfig as QuestionnaireTempInputConfigOptions),
        }),
  };
};

const encodeSection = (
  section: QuestionnaireTempSection | IQuestionnaireSection,
): QuestionnaireDraftSection => {
  const { id, questionnaireId, index, displayStrategyId } = section;
  return {
    ...pickDefined({ questionnaireId, index, displayStrategyId }),
    id,
    legend: section.legend,
    description: section.description,
    ...encodeMediaUrl(section.mediaUrl),
    questionList: (section.questionList ?? []).map(encodeQuestion),
  };
};

const encodeRule = (rule: UpsertDisplayRule): QuestionnaireDraftRule => {
  const result: QuestionnaireDraftRule = {};
  if (rule.source !== undefined) {
    result.source = rule.source && {
      type: rule.source.type,
      ...pickDefined({
        questionnaireQuestionId: rule.source.questionnaireQuestionId,
        questionnaireSectionId: rule.source.questionnaireSectionId,
      }),
    };
  }
  if (rule.condition !== undefined) {
    const condition = rule.condition;
    result.condition = condition && {
      type: condition.type,
      ...pickDefined({ operator: condition.operator }),
      ...(condition.inputNumberList
        ? {
            inputNumberList: condition.inputNumberList.map(({ operator, value }) => ({
              operator,
              value: finiteOrNull(value),
            })),
          }
        : {}),
      ...(condition.inputOptionList
        ? {
            inputOptionList: condition.inputOptionList.map(({ optionId }) =>
              pickDefined({ optionId }),
            ),
          }
        : {}),
    };
  }
  if (rule.target !== undefined) {
    result.target = rule.target && {
      type: rule.target.type,
      ...pickDefined({
        questionnaireSectionId: rule.target.questionnaireSectionId,
        questionnaireQuestionId: rule.target.questionnaireQuestionId,
      }),
    };
  }
  return result;
};

const encodeDisplayStrategyItem = (
  item: TempQuestionnaireDisplayStrategyUpsertItem,
): QuestionnaireDraftDisplayStrategyItem => ({
  ...pickDefined({
    sectionId: item.sectionId,
    questionId: item.questionId,
    displayStrategyId: item.displayStrategyId,
  }),
  displayStrategy: pickDefined({
    displayLogicList: item.displayStrategy.displayLogicList?.map(({ type, displayRuleList }) => ({
      type,
      ...(displayRuleList ? { displayRuleList: displayRuleList.map(encodeRule) } : {}),
    })),
  }),
});

// Built on first use, not as the module loads, and kept: the schema map builds a Joi schema per field,
// and every snapshot read encodes each schedule with it.
let scheduleScalarKeys: string[] | null = null;
const scheduleScalarKeyList = () =>
  (scheduleScalarKeys ??= [...Object.keys(getTaskScheduleSchemaMap()), "createdAt", "updatedAt"]);
const scheduleReminderKeyList = [
  "id",
  "taskScheduleId",
  "secondsBeforeExpiration",
  "title",
  "message",
];

const pickJsonFields = (source: Record<string, unknown>, keyList: string[]) => {
  const result: Record<string, unknown> = {};
  for (const key of keyList) {
    const value = source[key];
    if (value === undefined) continue;
    result[key] = value instanceof Date ? value.toISOString() : value;
  }
  return result;
};

const encodeScheduleValues = (values: Partial<ITaskSchedule>): QuestionnaireDraftScheduleValues => {
  const result = pickJsonFields(values as Record<string, unknown>, scheduleScalarKeyList());
  if (values.reminderList) {
    result.reminderList = values.reminderList.map((reminder) =>
      pickJsonFields(reminder as unknown as Record<string, unknown>, scheduleReminderKeyList),
    );
  }
  return result;
};

// queue items keep the user's intended action only; request progress is never part of them
const encodeSchedule = (schedule: ActionableTempTaskSchedule): QuestionnaireDraftSchedule => ({
  tempId: schedule.tempId,
  createdAt: new Date(schedule.createdAt).toISOString(),
  action: schedule.action,
  reminderListChanged: schedule.reminderListChanged ?? null,
  values: encodeScheduleValues(schedule.values),
});

const encodeDisplayLogicEditor = (
  displayLogic: DisplayLogicEditorDraftState,
): QuestionnaireDraftDisplayLogicEditor => ({
  targetType: displayLogic.targetType,
  itemId: displayLogic.itemId,
  enabledDisplayLogic: displayLogic.enabledDisplayLogic,
  enabledQuestionSkipLogic: displayLogic.enabledQuestionSkipLogic,
  afterThisSection: displayLogic.afterThisSection,
  displayRuleList: displayLogic.displayRuleList.map(encodeRule),
  skipRuleList: displayLogic.skipRuleList.map(encodeRule),
  displayRuleControlsList: displayLogic.displayRuleControlsList.map((controls) => ({
    sourceQuestionId: controls.sourceQuestionId,
    conditionOptionId: controls.conditionOptionId ?? null,
    conditionOperator: controls.conditionOperator,
    conditionValue: finiteOrNull(controls.conditionValue),
    conditionValue2: finiteOrNull(controls.conditionValue2),
  })),
  skipRuleControlsList: displayLogic.skipRuleControlsList.map((controls) => ({
    conditionOperator: controls.conditionOperator,
    conditionValue: finiteOrNull(controls.conditionValue),
    conditionValue2: finiteOrNull(controls.conditionValue2),
    targetValue: controls.targetValue,
  })),
});

export const createQuestionnaireDraftSnapshot = (
  working: QuestionnaireDraftWorkingState,
): QuestionnaireDraftSnapshot => {
  const { questionnaire, editor, form, schedule } = working;
  const effectiveSectionList = (
    editor ? editor.sectionList : (questionnaire?.sectionList ?? [])
  ).map(encodeSection);
  const committedSectionList =
    editor && questionnaire ? questionnaire.sectionList.map(encodeSection) : null;

  return {
    version: QUESTIONNAIRE_DRAFT_SCHEMA_VERSION,
    target: { ...working.target },
    revision: working.revision,
    ...(working.createKey ? { createKey: working.createKey } : {}),
    base: cloneDeep(working.base),
    form: {
      name: form.name ?? "",
      description: form.description ?? "",
      scheduleRepeat: form.scheduleRepeat ?? null,
      scheduleDescription: form.scheduleDescription ?? null,
      reminderList: form.reminderList?.map(({ type, value }) => ({ type, value })) ?? null,
      color: form.color,
      scheduleSettingType: form.scheduleSettingType,
    },
    schedule: {
      baselineScheduleIdList: [...schedule.baselineScheduleIdList],
      taskScheduleQueue: schedule.taskScheduleQueue.map(encodeSchedule),
      liveEditingScheduleQueue: schedule.liveEditingScheduleQueue.map(encodeSchedule),
    },
    questionnaire: questionnaire
      ? {
          ...pickDefined({
            id: "id" in questionnaire ? questionnaire.id : undefined,
            createdAt:
              "createdAt" in questionnaire ? toIsoString(questionnaire.createdAt) : undefined,
            updatedAt:
              "updatedAt" in questionnaire ? toIsoString(questionnaire.updatedAt) : undefined,
          }),
          taskId: questionnaire.taskId,
          projectId: questionnaire.projectId,
          workspaceId: questionnaire.workspaceId,
          name: questionnaire.name,
          description: questionnaire.description,
          sectionList: effectiveSectionList,
        }
      : null,
    displayStrategyList: working.displayStrategyList.map(encodeDisplayStrategyItem),
    editor: editor
      ? {
          rollbackSectionList:
            committedSectionList && !isEqual(committedSectionList, effectiveSectionList)
              ? committedSectionList
              : null,
          editingBlockId: editor.editingBlockId,
          inputBufferList: editor.inputBufferList.map(({ field, text }) => ({ field, text })),
          displayLogic: editor.displayLogic ? encodeDisplayLogicEditor(editor.displayLogic) : null,
        }
      : null,
  };
};

/* ------------------------------ restoring ------------------------------ */

const restoreRule = (rule: QuestionnaireDraftRule): UpsertDisplayRule => {
  const { condition, ...rest } = cloneDeep(rule);
  if (!condition)
    return { ...rest, ...(condition === undefined ? {} : { condition }) } as UpsertDisplayRule;
  return {
    ...rest,
    condition: {
      ...condition,
      ...(condition.inputNumberList
        ? {
            inputNumberList: condition.inputNumberList.map(({ operator, value }) => ({
              operator,
              value: nullToNaN(value),
            })),
          }
        : {}),
    },
  } as UpsertDisplayRule;
};

const restoreSchedule = (schedule: QuestionnaireDraftSchedule): ActionableTempTaskSchedule => ({
  tempId: schedule.tempId,
  createdAt: new Date(schedule.createdAt),
  action: schedule.action,
  ...(schedule.reminderListChanged === null
    ? {}
    : { reminderListChanged: schedule.reminderListChanged }),
  values: cloneDeep(schedule.values) as Partial<ITaskSchedule>,
});

const restoreDisplayStrategyItem = (
  item: QuestionnaireDraftDisplayStrategyItem,
): TempQuestionnaireDisplayStrategyUpsertItem => ({
  ...pickDefined({
    sectionId: item.sectionId,
    questionId: item.questionId,
    displayStrategyId: item.displayStrategyId,
  }),
  displayStrategy: pickDefined({
    displayLogicList: item.displayStrategy.displayLogicList?.map(({ type, displayRuleList }) => ({
      type,
      ...(displayRuleList ? { displayRuleList: displayRuleList.map(restoreRule) } : {}),
    })),
  }),
});

const restoreDisplayLogicEditor = (
  displayLogic: QuestionnaireDraftDisplayLogicEditor,
): DisplayLogicEditorDraftState => ({
  targetType: displayLogic.targetType,
  itemId: displayLogic.itemId,
  enabledDisplayLogic: displayLogic.enabledDisplayLogic,
  enabledQuestionSkipLogic: displayLogic.enabledQuestionSkipLogic,
  afterThisSection: displayLogic.afterThisSection,
  displayRuleList: displayLogic.displayRuleList.map(restoreRule),
  skipRuleList: displayLogic.skipRuleList.map(restoreRule),
  displayRuleControlsList: displayLogic.displayRuleControlsList.map((controls) => ({
    sourceQuestionId: controls.sourceQuestionId,
    ...(controls.conditionOptionId === null
      ? {}
      : { conditionOptionId: controls.conditionOptionId }),
    conditionOperator: controls.conditionOperator,
    conditionValue: nullToNaN(controls.conditionValue),
    conditionValue2: nullToNaN(controls.conditionValue2),
  })),
  skipRuleControlsList: displayLogic.skipRuleControlsList.map((controls) => ({
    conditionOperator: controls.conditionOperator,
    conditionValue: nullToNaN(controls.conditionValue),
    conditionValue2: nullToNaN(controls.conditionValue2),
    targetValue: controls.targetValue,
  })),
});

const nullToUndefined = <T>(value: T | null) => (value === null ? undefined : value);

// Turns a decoded snapshot back into editor state. It never reads the target or the base to decide where
// anything is written: those come from the editor that applies it.
export const restoreQuestionnaireDraftWorkingState = (
  snapshot: QuestionnaireDraftSnapshot,
): QuestionnaireDraftWorkingState => {
  const { questionnaire, editor, form, schedule } = snapshot;
  const effectiveSectionList = questionnaire?.sectionList ?? [];
  const committedSectionList = editor?.rollbackSectionList ?? effectiveSectionList;

  return {
    target: { ...snapshot.target },
    revision: snapshot.revision,
    ...(snapshot.createKey ? { createKey: snapshot.createKey } : {}),
    base: cloneDeep(snapshot.base),
    form: {
      ...pickDefined({
        name: form.name,
        description: form.description,
        scheduleRepeat: nullToUndefined(form.scheduleRepeat),
        scheduleDescription: nullToUndefined(form.scheduleDescription),
        reminderList: nullToUndefined(form.reminderList)?.map(({ type, value }) => ({
          type,
          value,
        })),
      }),
      color: form.color,
      scheduleSettingType: form.scheduleSettingType,
    },
    schedule: {
      baselineScheduleIdList: [...schedule.baselineScheduleIdList],
      taskScheduleQueue: schedule.taskScheduleQueue.map(restoreSchedule),
      liveEditingScheduleQueue: schedule.liveEditingScheduleQueue.map(restoreSchedule),
    },
    questionnaire: questionnaire
      ? ({
          ...cloneDeep(questionnaire),
          sectionList: cloneDeep(committedSectionList),
        } as TempQuestionnaire)
      : null,
    displayStrategyList: snapshot.displayStrategyList.map(restoreDisplayStrategyItem),
    editor: editor
      ? {
          sectionList: cloneDeep(effectiveSectionList) as QuestionnaireTempSection[],
          editingBlockId: editor.editingBlockId,
          inputBufferList: editor.inputBufferList.map(({ field, text }) => ({ field, text })),
          displayLogic: editor.displayLogic ? restoreDisplayLogicEditor(editor.displayLogic) : null,
        }
      : null,
  };
};

/* ------------------------------ saved base fingerprint ------------------------------ */

const sortKeysDeep = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (value instanceof Date) return value.toISOString();
  if (isPlainObject(value)) {
    const record = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(record).sort()) {
      if (record[key] !== undefined) result[key] = sortKeysDeep(record[key]);
    }
    return result;
  }
  return value;
};

const byIndexThenId = (a: { index?: number; id: string }, b: { index?: number; id: string }) =>
  (a.index ?? 0) - (b.index ?? 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

// The fingerprint covers what a restore would overwrite: questionnaire content, its display rules, the task
// form fields and the current schedules. It is computed from saved task data alone, so the copy the editor
// opened and a freshly fetched copy give the same value when nothing was saved in between. The task
// timestamp is not enough on its own: saving display rules does not change it.
export const createQuestionnaireDraftSavedBase = (task: ITask): QuestionnaireDraftSavedBase => {
  const questionnaire = task.questionnaire;
  const sortedQuestionnaire = questionnaire && {
    ...questionnaire,
    sectionList: [...(questionnaire.sectionList ?? [])].sort(byIndexThenId).map((section) => ({
      ...section,
      questionList: [...(section.questionList ?? [])].sort(byIndexThenId),
    })),
  };
  const canonical = {
    form: {
      name: questionnaire?.name ?? null,
      description: questionnaire?.description ?? null,
      color: task.color ?? null,
      scheduleRepeat: task.scheduleRepeat ?? null,
      scheduleDescription: task.scheduleDescription ?? null,
      reminderList: (task.reminderList ?? []).map(({ type, value }) => ({ type, value })),
    },
    scheduleList: (task.scheduleList ?? [])
      .filter((schedule) => schedule.current)
      .map((schedule) => {
        const values = encodeScheduleValues(schedule);
        delete values.createdAt;
        delete values.updatedAt;
        return values;
      })
      .sort((a, b) => String(a.id).localeCompare(String(b.id))),
    sectionList: (sortedQuestionnaire?.sectionList ?? []).map((section) => ({
      id: section.id,
      legend: section.legend,
      description: section.description,
      mediaUrl: section.mediaUrl ?? null,
      questionList: section.questionList.map((question) => ({
        id: question.id,
        type: question.type,
        label: question.label,
        description: question.description,
        required: question.required,
        mediaUrl: question.mediaUrl ?? null,
        inputConfig: question.inputConfig ?? null,
      })),
    })),
    displayStrategyList: sortedQuestionnaire
      ? fetchDisplayStrategyListFromQuestionnaire(sortedQuestionnaire)
      : [],
  };
  return {
    taskUpdatedAt: toIsoString(task.updatedAt),
    fingerprint: hashString(JSON.stringify(sortKeysDeep(canonical))),
  };
};

// The questions and display rules of a copy, as the copy encodes them, without the fields that only say
// where each part is stored or in which slot it sits. An unchanged copy and the saved task it came from
// give the same value.
const questionnaireStructureOf = (
  sectionList: QuestionnaireDraftSection[],
  displayStrategyList: QuestionnaireDraftDisplayStrategyItem[],
): string =>
  JSON.stringify(
    sortKeysDeep({
      sectionList: sectionList.map((section) => ({
        ...omit(section, ["questionnaireId", "index", "displayStrategyId"]),
        questionList: section.questionList.map((question) =>
          omit(question, [
            "questionnaireId",
            "questionnaireSectionId",
            "index",
            "displayStrategyId",
          ]),
        ),
      })),
      displayStrategyList,
    }),
  );

// Whether the copy's questions or display rules differ from the saved task's: what a restore cannot
// apply where they are locked. Both sides keep the order the API gave, which is the order the editor
// loads and edits, so a reordered question counts as a difference. A display-rule editor open in the
// copy may hold edits not committed to its rule list yet, so it counts as one too.
export const questionnaireDraftChangesQuestions = (
  snapshot: QuestionnaireDraftSnapshot,
  task: ITask,
): boolean => {
  const savedQuestionnaire = task.questionnaire;
  const saved = questionnaireStructureOf(
    (savedQuestionnaire?.sectionList ?? []).map(encodeSection),
    savedQuestionnaire
      ? fetchDisplayStrategyListFromQuestionnaire(savedQuestionnaire).map(encodeDisplayStrategyItem)
      : [],
  );
  const copied = questionnaireStructureOf(
    snapshot.questionnaire?.sectionList ?? [],
    snapshot.displayStrategyList,
  );
  return copied !== saved || Boolean(snapshot.editor?.displayLogic);
};

/* ------------------------------ decoding ------------------------------ */

export type QuestionnaireDraftDecodeResult =
  | { ok: true; snapshot: QuestionnaireDraftSnapshot }
  | { ok: false; reason: "unsupported_version" | "malformed" };

let snapshotSchema: Joi.ObjectSchema | null = null;

// built on first use rather than at module load, so the enums it reads are initialised
const getSnapshotSchema = () => {
  if (snapshotSchema) return snapshotSchema;

  const text = Joi.string().allow("");
  const id = Joi.string();
  const finiteNumberOrNull = Joi.number().allow(null);
  const values = <T extends object>(enumObject: T) => Object.values(enumObject) as string[];

  const savedBase = Joi.object({
    taskUpdatedAt: Joi.string().required(),
    fingerprint: Joi.string().required(),
  });

  const option = Joi.object({ id, value: text.required(), name: text.required() });
  const inputConfig = Joi.object({
    customOtherName: text,
    allowOther: Joi.boolean(),
    showSlider: Joi.boolean(),
    integer: Joi.boolean(),
    max: Joi.number(),
    min: Joi.number(),
    maxLabel: text,
    minLabel: text,
    options: Joi.array().items(option),
  });
  const question = Joi.object({
    id: id.required(),
    questionnaireId: id,
    questionnaireSectionId: id,
    index: Joi.number().integer(),
    displayStrategyId: id,
    label: text.required(),
    type: Joi.string()
      .valid(...values(QuestionnaireQuestionType))
      .required(),
    description: text.required(),
    required: Joi.boolean().required(),
    mediaUrl: text.allow(null),
    inputConfig: inputConfig.allow(null),
  });
  const section = Joi.object({
    id: id.required(),
    questionnaireId: id,
    index: Joi.number().integer(),
    displayStrategyId: id,
    legend: text.required(),
    description: text.required(),
    mediaUrl: text.allow(null),
    questionList: Joi.array().items(question).required(),
  });

  const rule = Joi.object({
    source: Joi.object({
      type: Joi.string()
        .valid(...values(IQuestionnaireDisplayRuleSourceType))
        .required(),
      questionnaireQuestionId: text,
      questionnaireSectionId: text,
    }).allow(null),
    condition: Joi.object({
      type: Joi.string()
        .valid(...values(IQuestionnaireDisplayRuleConditionType))
        .required(),
      operator: Joi.string().valid(...values(IQuestionnaireDisplayRuleConditionOperator)),
      inputNumberList: Joi.array().items(
        Joi.object({
          operator: Joi.string()
            .valid(...values(IQuestionnaireDisplayRuleConditionInputNumberOperator))
            .required(),
          value: finiteNumberOrNull.required(),
        }),
      ),
      inputOptionList: Joi.array().items(Joi.object({ optionId: text })),
    }).allow(null),
    target: Joi.object({
      type: Joi.string()
        .valid(...values(IQuestionnaireDisplayRuleTargetType))
        .required(),
      questionnaireSectionId: text,
      questionnaireQuestionId: text,
    }).allow(null),
  });
  const displayStrategyItem = Joi.object({
    sectionId: id,
    questionId: id,
    displayStrategyId: id,
    displayStrategy: Joi.object({
      displayLogicList: Joi.array().items(
        Joi.object({
          type: Joi.string()
            .valid(...values(IQuestionnaireDisplayLogicType))
            .required(),
          displayRuleList: Joi.array().items(rule),
        }),
      ),
    }).required(),
  });

  const scheduleReminder = Joi.object({
    id,
    taskScheduleId: id,
    secondsBeforeExpiration: Joi.number(),
    title: text,
    message: text,
  });
  const schedule = Joi.object({
    tempId: id.required(),
    createdAt: Joi.string().isoDate().required(),
    action: Joi.string()
      .valid(...values(TempTaskScheduleActionType))
      .allow(null)
      .required(),
    reminderListChanged: Joi.boolean().allow(null).required(),
    values: Joi.object({ reminderList: Joi.array().items(scheduleReminder) })
      .pattern(
        Joi.string().valid(...scheduleScalarKeyList()),
        Joi.alternatives(text, Joi.number(), Joi.boolean()).allow(null),
      )
      .required(),
  });

  snapshotSchema = Joi.object({
    version: Joi.number().valid(QUESTIONNAIRE_DRAFT_SCHEMA_VERSION).required(),
    target: Joi.alternatives(
      Joi.object({
        kind: Joi.string().valid("new").required(),
        projectId: id.required(),
        methodId: id.required(),
      }),
      Joi.object({
        kind: Joi.string().valid("existing").required(),
        projectId: id.required(),
        methodId: id.required(),
        taskId: id.required(),
      }),
    ).required(),
    revision: Joi.number().integer().min(0).required(),
    createKey: Joi.string().guid(),
    base: Joi.object({
      saved: savedBase.allow(null).required(),
      checkpoint: Joi.object({
        taskId: id.required(),
        confirmedStageList: Joi.array()
          .items(Joi.string().valid(...values(QuestionnaireDraftSaveStage)))
          .required(),
        base: savedBase.required(),
        idMap: Joi.object().pattern(Joi.string(), Joi.string()).required(),
      })
        .allow(null)
        .required(),
      submission: Joi.object({
        revision: Joi.number().integer().min(0).required(),
        fingerprint: id.required(),
      })
        .allow(null)
        .required(),
    }).required(),
    form: Joi.object({
      name: text.required(),
      description: text.required(),
      scheduleRepeat: Joi.string().valid("repeat", "oneTime").allow(null).required(),
      scheduleDescription: text.allow(null).required(),
      reminderList: Joi.array()
        .items(
          Joi.object({
            type: Joi.string()
              .valid(...values(ReminderType))
              .required(),
            value: Joi.string().required(),
          }),
        )
        .allow(null)
        .required(),
      color: Joi.string().required(),
      scheduleSettingType: Joi.string()
        .valid(...values(TaskScheduleSettingType))
        .required(),
    }).required(),
    schedule: Joi.object({
      baselineScheduleIdList: Joi.array().items(id).required(),
      taskScheduleQueue: Joi.array().items(schedule).required(),
      liveEditingScheduleQueue: Joi.array().items(schedule).required(),
    }).required(),
    questionnaire: Joi.object({
      id,
      createdAt: Joi.string(),
      updatedAt: Joi.string(),
      taskId: id.required(),
      projectId: text.required(),
      workspaceId: text.required(),
      name: text.required(),
      description: text.required(),
      sectionList: Joi.array().items(section).required(),
    })
      .allow(null)
      .required(),
    displayStrategyList: Joi.array().items(displayStrategyItem).required(),
    editor: Joi.object({
      rollbackSectionList: Joi.array().items(section).min(1).allow(null).required(),
      editingBlockId: id.allow(null).required(),
      inputBufferList: Joi.array()
        .items(Joi.object({ field: id.required(), text: text.required() }))
        .required(),
      displayLogic: Joi.object({
        targetType: Joi.string().valid("section", "question").required(),
        itemId: id.required(),
        enabledDisplayLogic: Joi.boolean().required(),
        enabledQuestionSkipLogic: Joi.boolean().required(),
        afterThisSection: id.required(),
        displayRuleList: Joi.array().items(rule).required(),
        skipRuleList: Joi.array().items(rule).required(),
        displayRuleControlsList: Joi.array()
          .items(
            Joi.object({
              sourceQuestionId: text.required(),
              conditionOptionId: text.allow(null).required(),
              conditionOperator: id.required(),
              conditionValue: finiteNumberOrNull.required(),
              conditionValue2: finiteNumberOrNull.required(),
            }),
          )
          .required(),
        skipRuleControlsList: Joi.array()
          .items(
            Joi.object({
              conditionOperator: id.required(),
              conditionValue: finiteNumberOrNull.required(),
              conditionValue2: finiteNumberOrNull.required(),
              targetValue: id.required(),
            }),
          )
          .required(),
      })
        .allow(null)
        .required(),
    })
      .allow(null)
      .required(),
  });
  return snapshotSchema;
};

// relationships a field-by-field schema cannot express, each of which would break hydration
const isConsistent = (snapshot: QuestionnaireDraftSnapshot) => {
  const { target, base, questionnaire, editor } = snapshot;
  if ((target.kind === "existing") !== (base.saved !== null)) return false;
  if (!editor) return true;
  if (!questionnaire || questionnaire.sectionList.length === 0) return false;
  const displayLogic = editor.displayLogic;
  if (!displayLogic) return true;
  return displayLogic.targetType === "section"
    ? questionnaire.sectionList.some(({ id }) => id === displayLogic.itemId)
    : questionnaire.sectionList.some(({ questionList }) =>
        questionList.some(({ id }) => id === displayLogic.itemId),
      );
};

// Validates a stored copy before anything hydrates from it. A failure carries only a reason: the validation
// message would quote the questionnaire content back.
export const decodeQuestionnaireDraft = (raw: unknown): QuestionnaireDraftDecodeResult => {
  if (!isPlainObject(raw)) return { ok: false, reason: "malformed" };
  if ((raw as { version?: unknown }).version !== QUESTIONNAIRE_DRAFT_SCHEMA_VERSION) {
    return { ok: false, reason: "unsupported_version" };
  }
  const { error, value } = getSnapshotSchema().validate(raw, { abortEarly: true, convert: false });
  if (error || !isConsistent(value as QuestionnaireDraftSnapshot))
    return { ok: false, reason: "malformed" };
  return { ok: true, snapshot: cloneDeep(value) as QuestionnaireDraftSnapshot };
};

/* ------------------------------ assessing a copy before restoring it ------------------------------ */

export interface SavedQuestionnaireSummary {
  taskId: string;
  name: string;
  updatedAt: string;
}

export const savedQuestionnaireSummaryOf = (task: ITask): SavedQuestionnaireSummary => ({
  taskId: task.id,
  name: task.questionnaire?.name ?? "",
  updatedAt: toIsoString(task.updatedAt),
});

export type QuestionnaireDraftRestoreAssessment =
  // judging it needs the method's saved tasks, freshly fetched
  | { kind: "needs_saved_tasks"; snapshot: QuestionnaireDraftSnapshot }
  // another schema version, or not a copy at all: it never enters the editor
  | { kind: "unsupported" }
  // stored for another task or another method's editor; it is never restored here, or redirected
  | { kind: "other_target" }
  // `questionsChanged`: the copy's questions or display rules differ from the saved task's
  | { kind: "offer"; snapshot: QuestionnaireDraftSnapshot; questionsChanged: boolean }
  // the saved questionnaire changed, or went, since the copy was made
  | {
      kind: "conflict";
      snapshot: QuestionnaireDraftSnapshot;
      reason: "changed" | "missing";
      questionsChanged: boolean;
    }
  // a create was sent and may have gone through: saved work has to be looked at before another one
  | {
      kind: "review_saved_work";
      snapshot: QuestionnaireDraftSnapshot;
      savedList: SavedQuestionnaireSummary[];
    };

const sameTarget = (a: QuestionnaireDraftTarget, b: QuestionnaireDraftTarget) =>
  a.kind === b.kind &&
  a.projectId === b.projectId &&
  a.methodId === b.methodId &&
  (a.kind === "new" || (b.kind === "existing" && a.taskId === b.taskId));

const sameSavedBase = (a: QuestionnaireDraftSavedBase, b: QuestionnaireDraftSavedBase) =>
  a.taskUpdatedAt === b.taskUpdatedAt && a.fingerprint === b.fingerprint;

// What may be done with a copy fetched for the editor opened on `target`. The target and the saved
// base the copy names are checked against the editor's own context and against `savedTaskList`, the
// method's tasks as just fetched, so a copy can neither land in another editor nor quietly replace
// saved work that changed after it was made. The task timestamp alone would not show that: saving
// only the display rules leaves it as it was, so the fingerprint is compared as well.
export const assessQuestionnaireDraftRestore = ({
  draft,
  target,
  savedTaskList,
}: {
  draft: unknown;
  target: QuestionnaireDraftTarget;
  savedTaskList: ITask[] | null;
}): QuestionnaireDraftRestoreAssessment => {
  const decoded = decodeQuestionnaireDraft(draft);
  if (!decoded.ok) return { kind: "unsupported" };
  const { snapshot } = decoded;
  if (!sameTarget(snapshot.target, target)) return { kind: "other_target" };

  const { checkpoint, saved, submission } = snapshot.base;
  if (checkpoint && target.kind === "existing" && checkpoint.taskId !== target.taskId)
    return { kind: "other_target" };
  if (target.kind === "new" && !checkpoint && !submission)
    return { kind: "offer", snapshot, questionsChanged: false };
  if (!savedTaskList) return { kind: "needs_saved_tasks", snapshot };

  // A create was sent, and may have gone through; or it did, and a later stage failed. Either way a
  // restore into a new questionnaire's editor could create it twice.
  if (target.kind === "new") {
    const savedList = savedTaskList
      .filter((task) => task.type === TaskType.Questionnaire)
      .map(savedQuestionnaireSummaryOf)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    return { kind: "review_saved_work", snapshot, savedList };
  }

  // a partial save of the copy's own is where the saved task now stands; otherwise where the edit began
  const taskId = checkpoint ? checkpoint.taskId : target.taskId;
  const expected = checkpoint ? checkpoint.base : saved;
  const current = savedTaskList.find((task) => task.id === taskId);
  if (!current || !expected)
    return { kind: "conflict", snapshot, reason: "missing", questionsChanged: false };
  const questionsChanged = questionnaireDraftChangesQuestions(snapshot, current);
  if (!sameSavedBase(expected, createQuestionnaireDraftSavedBase(current))) {
    return { kind: "conflict", snapshot, reason: "changed", questionsChanged };
  }
  return { kind: "offer", snapshot, questionsChanged };
};
